import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, UserCircle2, LayoutGrid, Settings2, LogOut, CircleHelp, Undo2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import MeldingenCentrum from "@/components/MeldingenCentrum";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LayoutV2MenuItems, LayoutV2TestersHost } from "@/components/layout_v2/LayoutV2Schakelaar";
import { useLayoutV3 } from "@/hooks/useLayoutV3";
import { rolLabel } from "@/lib/rollen";
import { formatErr } from "@/lib/api";
import { initialsFrom } from "@/components/WelkomOverlay";
import { V3Zoek } from "./V3Zoek";
import { MijnTakenBadge } from "./MijnTakenBadge";

/** Menu-item om tussen Layout v3 en de vorige layout te wisselen (in beide bovenbalken). */
export function LayoutV3MenuItem() {
  const v3 = useLayoutV3();
  if (!v3.mag_schakelen) return null;
  const naarOud = v3.actief;
  const wissel = async () => {
    try { await v3.zet(naarOud ? "oud" : (v3.flag_aan ? null : "v3")); toast.success(naarOud ? "Vorige layout staat weer aan voor jou" : "Nieuwe layout staat aan voor jou"); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail || e?.message)); }
  };
  return (
    <DropdownMenuItem onSelect={wissel} data-testid="v3-layout-wissel" className="gap-2">
      {naarOud ? <Undo2 className="h-4 w-4" /> : <Sparkles className="h-4 w-4 text-orange-500" />}
      {naarOud ? "Vorige layout gebruiken" : "Nieuwe layout (v3) gebruiken"}
    </DropdownMenuItem>
  );
}

/** Knop in de (oude) menulade om terug te gaan naar Layout v3. */
export function LayoutV3Knop() {
  const v3 = useLayoutV3();
  if (!v3.mag_schakelen || v3.actief) return null;
  const aan = async () => {
    try { await v3.zet(v3.flag_aan ? null : "v3"); toast.success("Nieuwe layout staat aan voor jou"); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail || e?.message)); }
  };
  return (
    <button type="button" onClick={aan} className="flex items-center w-full px-3 py-1.5 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100" data-testid="v3-layout-terug-knop">
      <Sparkles className="h-4 w-4 mr-2 text-orange-500" /> Nieuwe layout (v3)
    </button>
  );
}

/** Layout v3 bovenbalk: globale zoekbalk, 'Mijn taken', meldingen, help en gebruikersmenu. */
export function V3TopBar({ hubs, user, onMenuInstellingen, onLogout }) {
  const nav = useNavigate();
  return (
    <header className="v3-topbar" data-testid="v3-topbar">
      <button type="button" className="v3-logo v3-mlogo" onClick={() => nav("/dashboard")} aria-label="Naar dashboard" data-testid="v3-mlogo">BPT</button>
      <V3Zoek hubs={hubs} />
      <span className="flex-1" />
      <MijnTakenBadge />
      <div data-testid="v3-meldingen"><MeldingenCentrum variant="desktop" /></div>
      <button type="button" className="v3-iconbtn v3-hide-m" onClick={() => nav("/handboek")} aria-label="Help / handboek" title="Handboek" data-testid="v3-help"><CircleHelp /></button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[var(--v3-soft)] transition-colors" aria-label="Gebruikersmenu" data-testid="v3-account-menu">
            <span className="v3-av">{initialsFrom(user?.name || "?")}</span>
            <span className="v3-hide-m text-left leading-tight hidden xl:block">
              <span className="block text-[13px] font-semibold max-w-[150px] truncate" style={{ color: "var(--v3-ink)" }}>{user?.name}</span>
              <span className="block text-[11px]" style={{ color: "var(--v3-ink-2)" }} data-testid="v3-rol-label">{rolLabel(user?.role)}</span>
            </span>
            <ChevronDown className="h-3.5 w-3.5" style={{ color: "var(--v3-ink-3)" }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid="v3-account-dropdown">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs opacity-70 truncate">{user?.email}</div>
            <div className="text-[11px] opacity-60 mt-0.5">{rolLabel(user?.role)}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => nav("/mijn-gegevens")} data-testid="v3-account-mijn-gegevens"><UserCircle2 className="h-4 w-4 mr-2" /> Mijn gegevens</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => nav("/alle-tools")} data-testid="v3-account-alle-tools"><LayoutGrid className="h-4 w-4 mr-2" /> Alle tools</DropdownMenuItem>
          <DropdownMenuItem onSelect={onMenuInstellingen} data-testid="v3-account-menu-aanpassen" data-rechten="lezen"><Settings2 className="h-4 w-4 mr-2" /> Menu aanpassen</DropdownMenuItem>
          <DropdownMenuSeparator />
          <LayoutV3MenuItem />
          <LayoutV2MenuItems />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onLogout} className="text-red-700 focus:text-red-800" data-testid="v3-account-logout"><LogOut className="h-4 w-4 mr-2" /> Uitloggen</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LayoutV2TestersHost />
    </header>
  );
}
